// src/components/ClassRecordList.js
import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom'; // Import Link
import styles from './ClassRecordList.module.css';
import * as XLSX from 'xlsx';
import Modal from './Modal';
import { saveAs } from 'file-saver';
import { ReactComponent as ImportIcon } from '../icon/导入.svg';
import { ReactComponent as ExportIcon } from '../icon/导出.svg';
import { ReactComponent as EditIcon } from '../icon/编辑.svg';
import { ReactComponent as DeleteIcon } from '../icon/删除.svg';
import { ReactComponent as CountIcon } from '../icon/次数.svg';
import { ReactComponent as MemberIcon } from '../icon/人数.svg';
import { ReactComponent as RevenueIcon } from '../icon/总收入.svg';

function ClassRecordList({ records, onEdit, onDelete, onImport }) {
  const fileInputRef = useRef(null);
  const [selectedProject, setSelectedProject] = useState('项目');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [projectOptions, setProjectOptions] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('default'); // 'default', 'byPerson'
  const [personSortConfig, setPersonSortConfig] = useState({ key: 'classCount', direction: 'descending' }); // For sorting in 'byPerson' view, default to classCount descending

  // State for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDeleteId, setRecordToDeleteId] = useState(null);

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(14); // Default to 15 items per page
  const [itemsPerPageInput, setItemsPerPageInput] = useState(14); // State for the input field value

  useEffect(() => {
    const uniqueProjects = [...new Set(records.flatMap(record => record.content || []))];
    setProjectOptions(['项目', ...uniqueProjects]);
  }, [records]);

  // Reset to first page when filters or sorting change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProject, selectedMethod, sortConfig]);

  // Update input state when itemsPerPage changes (e.g., initially or after valid input)
  useEffect(() => {
    setItemsPerPageInput(itemsPerPage);
  }, [itemsPerPage]);


  const sortedAndFilteredRecords = useMemo(() => {
    let processedRecords = [...records];

    // Apply search filter first
    if (searchTerm) {
      processedRecords = processedRecords.filter(record =>
        record.person.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply other filters
    if (selectedProject !== '项目') {
      processedRecords = processedRecords.filter(record => record.content && record.content.includes(selectedProject));
    }
    if (selectedMethod !== '') {
      processedRecords = processedRecords.filter(record => record.method === selectedMethod);
    }

    const lessonCount = processedRecords.length;
    const uniqueStudents = new Set(processedRecords.map(record => record.person));
    const uniqueStudentsCount = uniqueStudents.size;
    const totalRevenue = processedRecords.reduce((sum, record) => {
      const fee = parseFloat(record.fee);
      return sum + (isNaN(fee) ? 0 : fee);
    }, 0);

    if (viewMode === 'byPerson') {
      const stats = processedRecords.reduce((acc, record) => {
        if (!acc[record.person]) {
          acc[record.person] = { person: record.person, classCount: 0, totalFee: 0, projects: new Set(), methods: new Set() };
        }
        acc[record.person].classCount += 1;
        acc[record.person].totalFee += (parseFloat(record.fee) || 0);
        if (record.content) {
          record.content.forEach(project => acc[record.person].projects.add(project));
        }
        if (record.method) {
          acc[record.person].methods.add(record.method);
        }
        return acc;
      }, {});
      processedRecords = Object.values(stats).map(item => ({
        person: item.person,
        classCount: item.classCount,
        totalFee: item.totalFee,
        projects: Array.from(item.projects).join(' / '),
        method: item.methods.size > 1 ? '混合' : (item.methods.size === 1 ? Array.from(item.methods)[0] : '-')
      }));

      // Apply sorting for 'byPerson' view
      if (personSortConfig.key !== null) {
        processedRecords.sort((a, b) => {
          let aValue = a[personSortConfig.key];
          let bValue = b[personSortConfig.key];

          if (personSortConfig.key === 'person') {
            return personSortConfig.direction === 'ascending' ?
                   String(aValue).localeCompare(String(bValue)) :
                   String(bValue).localeCompare(String(aValue));
          } else if (personSortConfig.key === 'classCount' || personSortConfig.key === 'totalFee') {
            return personSortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
          }
          return 0;
        });
      }
    } else {
      // Apply default sorting
      if (sortConfig.key !== null) {
        processedRecords.sort((a, b) => {
          let aValue = a[sortConfig.key];
          let bValue = b[sortConfig.key];

          if (sortConfig.key === 'record_time') {
              aValue = new Date(aValue).getTime();
              bValue = new Date(bValue).getTime();
          } else if (sortConfig.key === 'person') {
              return sortConfig.direction === 'ascending' ?
                     String(aValue).localeCompare(String(bValue)) :
                     String(bValue).localeCompare(String(aValue));
          }

          if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
          if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
          return 0;
        });
      }
    }

    return {
      filteredRecords: processedRecords,
      lessonCount,
      uniqueStudentsCount,
      totalRevenue
    };
  }, [records, selectedProject, selectedMethod, sortConfig, searchTerm, viewMode, personSortConfig]);

  const { filteredRecords, lessonCount, uniqueStudentsCount, totalRevenue } = sortedAndFilteredRecords;

  // Pagination logic
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRecords = filteredRecords.slice(startIndex, endIndex);

  const handleProjectChange = (event) => {
    setSelectedProject(event.target.value);
    setViewMode('default'); // Reset view when filters change
  };

  const handleMethodChange = (event) => {
    setSelectedMethod(event.target.value);
    setViewMode('default'); // Reset view when filters change
  };

  const handleToggleViewMode = () => {
    setViewMode(prevMode => {
      const newMode = prevMode === 'default' ? 'byPerson' : 'default';
      // When switching to 'byPerson' view, set default sort to 'classCount' descending
      // When switching back to 'default' view, reset personSortConfig
      setPersonSortConfig(newMode === 'byPerson' ? { key: 'classCount', direction: 'descending' } : { key: null, direction: 'ascending' });
      return newMode;
    });
  };

  const requestPersonSort = useCallback((key) => {
    let direction = 'ascending';
    if (personSortConfig.key === key && personSortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setPersonSortConfig({ key, direction });
  }, [personSortConfig]);

  const handleItemsPerPageChange = (event) => {
    // Update the input state in real-time
    setItemsPerPageInput(event.target.value);
  };

  const handleItemsPerPageKeyDown = (event) => {
    if (event.key === 'Enter') {
      const value = parseInt(itemsPerPageInput, 10);
      if (!isNaN(value) && value > 0) {
        setItemsPerPage(value);
        setCurrentPage(1); // Reset to first page when items per page changes
      } else {
        // Optionally revert input to the current valid itemsPerPage if invalid input was entered
        setItemsPerPageInput(itemsPerPage);
      }
    }
  };

  const goToNextPage = useCallback(() => {
    setCurrentPage(prevPage => Math.min(prevPage + 1, totalPages));
  }, [totalPages]);

  const goToPreviousPage = useCallback(() => {
    setCurrentPage(prevPage => Math.max(prevPage - 1, 1));
  }, []);

  const requestSort = useCallback((key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  }, [sortConfig]);

  const getMethodClassName = useCallback((method) => {
    switch (method) {
      case '线上': return styles.methodOnline;
      case '线下': return styles.methodOffline;
      case '混合': return styles.methodMixed;
      case '包月': return styles.methodMonthly;
      default: return '';
    }
  }, []);

  // Function to handle delete button click (opens modal)
  const handleDeleteClick = useCallback((id) => {
    setRecordToDeleteId(id);
    setShowDeleteModal(true);
  }, []);

  const getSortIndicator = useCallback((key, currentSortConfig) => {
    if (currentSortConfig.key !== key) {
      return '';
    }
    return currentSortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
  }, []);

  const formatDateTime = useCallback((dateTimeString) => {
    if (!dateTimeString) return '';
    try {
        const date = new Date(dateTimeString);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const hours = ('0' + date.getHours()).slice(-2);
        const minutes = ('0' + date.getMinutes()).slice(-2);
        return `${year}/${month}/${day} ${hours}:${minutes}`;
    } catch (e) {
        console.error("Invalid date string:", dateTimeString, e);
        return String(dateTimeString);
    }
  }, []);

  const handleImportClick = useCallback(() => {
    fileInputRef.current.click();
  }, []);

  const handleFileChange = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const json = XLSX.utils.sheet_to_json(worksheet);

        console.log('Imported JSON:', json);

        if (onImport) {
           const importedRecords = json.map(row => ({
             // Adjust column names based on your XLSX file headers
             record_time: row['时间'],
             person: row['名字'],
             content: row['项目'] ? String(row['项目']).split(' / ') : [],
             method: row['方式'],
             fee: row['费用'] !== undefined ? parseFloat(row['费用']) : null,
             remarks: row['备注'],
             // ID will need to be generated or handled by the parent component
           }));
           onImport(importedRecords);
        } else {
            alert('导入功能未完全实现：缺少处理导入数据的函数');
        }
      };
      reader.readAsArrayBuffer(file);
    }
  }, [onImport]);

  const handleExport = useCallback(() => {
    console.log('Export button clicked');
    if (!records || records.length === 0) {
      alert('没有数据可导出');
      return;
    }

    const dataToExport = records.map(record => ({
      时间: formatDateTime(record.record_time),
      名字: record.person,
      项目: record.content ? record.content.join(' / ') : '-',
      方式: record.method,
      费用: record.fee !== null ? parseFloat(record.fee) : '-',
      备注: record.remarks || '-',
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "上课记录");
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

    // 获取当前日期，格式为250828
    const now = new Date();
    const y = String(now.getFullYear()).slice(-2);
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    const fileName = `上课记录${y}${m}${d}.xlsx`;

    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), fileName);
  }, [records, formatDateTime]);

  // Function to handle confirm delete button click (calls onDelete and closes modal)
  const confirmDelete = useCallback(() => {
    if (recordToDeleteId !== null) {
      onDelete(recordToDeleteId);
      setRecordToDeleteId(null);
      setShowDeleteModal(false);
    }
  }, [onDelete, recordToDeleteId]);


  return (
    <div className={styles.listContainer}>
      <div className={styles.headerContainer}>
        <h2>上课记录</h2>
        {/* Add the link to the statistics page */}
        <Link to="/statistics" className={styles.statisticsLink}>
          统计
        </Link>
        <div className={styles.buttonGroup}>
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept=".xlsx, .xls"
          />
          <button onClick={handleImportClick} title="导入">
            <ImportIcon width="16" height="16" />
          </button>
          <button onClick={handleExport} title="导出">
            <ExportIcon width="16" height="16" />
          </button>
        </div>
        <div className={styles.statistics}>
          <span title="上课人数"><MemberIcon width="20" height="20" />{uniqueStudentsCount}</span>
          <span title="上课课时"><CountIcon width="20" height="20" /> {lessonCount}</span>
          <span title="费用统计"><RevenueIcon width="20" height="20" /> ¥{totalRevenue}</span>
        </div>
      </div>

      <div className={styles.controlsContainer}>
        <div className={styles.searchInputContainer} style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="搜索姓名..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute',
                right: '6px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#d00',
                padding: '2px 6px',
                lineHeight: 1,
                transition: 'background 0.2s',
              }}
            >
              ×
            </button>
          )}
        </div>
        <button onClick={handleToggleViewMode} className={styles.controlButton}>
          {viewMode === 'default' ? '按人员显示' : '按时间显示'}
        </button>
      </div>

       {records && records.length > 0 && (
        <div className={styles.tableWrapper}>
          <table className={`${styles.recordsTable} ${viewMode === 'byPerson' ? styles.recordsTableByPerson : ''}`}>
            <thead>
              {viewMode === 'default' && (
                <tr>
                  <th onClick={() => requestSort('record_time')} className={styles.sortableHeader}>
                    时间{getSortIndicator('record_time', sortConfig)}
                  </th>
                  <th onClick={() => requestSort('person')} className={styles.sortableHeader}>
                    名字{getSortIndicator('person', sortConfig)}
                  </th>
                  <th>
                    <select value={selectedProject} onChange={handleProjectChange}>
                      {projectOptions.map(project => (
                        <option key={project} value={project}>{project}</option>
                      ))}
                    </select>
                  </th>
                  <th>
                    <select value={selectedMethod} onChange={handleMethodChange} className={styles.selectInput}>
                      <option value="">全部</option>
                      <option value="线上">线上</option>
                      <option value="线下">线下</option>
                      <option value="混合">混合</option>
                      <option value="包月">包月</option>
                    </select>
                  </th>
                  <th>费用</th>
                  <th>备注</th>
                  <th>操作</th>
                </tr>
              )}
              {viewMode === 'byPerson' && (
                <tr>
                  <th onClick={() => requestPersonSort('person')} className={styles.sortableHeader}>
                    人员{getSortIndicator('person', personSortConfig)}
                  </th>
                  <th onClick={() => requestPersonSort('classCount')} className={styles.sortableHeader}>
                    次数{getSortIndicator('classCount', personSortConfig)}
                  </th>
                  <th onClick={() => requestPersonSort('totalFee')} className={styles.sortableHeader}>
                    总费用{getSortIndicator('totalFee', personSortConfig)}
                  </th>
                  <th>项目</th>
                  <th>上课渠道</th>
                </tr>
              )}
            </thead>
            <tbody>
              {viewMode === 'default' && currentRecords.map(record => (
                <tr key={record.id}>
                  <td>{formatDateTime(record.record_time)}</td>
                  <td onClick={() => { setSearchTerm(record.person); setViewMode('default'); }} className={styles.personNameClickable}>{record.person}</td>
                  <td>{record.content ? record.content.join(' / ') : '-'}</td>
                  <td><div className={getMethodClassName(record.method)}>{record.method}</div></td>
                  <td>{record.fee !== null ? `¥${parseFloat(record.fee)}` : '-'}</td>
                  <td>{record.remarks || '-'}</td>
                  <td className={styles.actionsCell}>
                    <button onClick={() => onEdit(record)} className={styles.actionButton} style={{ marginRight: '5px' }} title="编辑">
                      <EditIcon width="16" height="16" />
                    </button>
                    <button onClick={() => handleDeleteClick(record.id)} className={`${styles.actionButton} ${styles.deleteButton}`} title="删除">
                      <DeleteIcon width="16" height="16" />
                    </button>
                  </td>
                </tr>
              ))}
              {viewMode === 'byPerson' && currentRecords.map(record => (
                <tr key={record.person}>
                  <td onClick={() => { setSearchTerm(record.person); setViewMode('default'); }} className={styles.personNameClickable}>{record.person}</td>
                  <td>{record.classCount}</td>
                  <td>¥{record.totalFee.toFixed(2)}</td>
                  <td>{record.projects || '-'}</td>
                  <td><div className={getMethodClassName(record.method)}>{record.method}</div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
       )}

      {/* Pagination Controls and Items per Page Input */}
      {records && records.length > 0 && (
        <div className={styles.paginationContainer}>
          <button onClick={goToPreviousPage} disabled={currentPage === 1}>上一页</button>
          <span>第 {currentPage} 页 / 共 {totalPages} 页</span>
          <button onClick={goToNextPage} disabled={currentPage === totalPages}>下一页</button>
          <div className={styles.itemsPerPageControl}>
            <span>每页显示:</span>
            <input
              type="number"
              min="1"
              value={itemsPerPageInput} // Bind value to the input state
              onChange={handleItemsPerPageChange} // Update input state on change
              onKeyDown={handleItemsPerPageKeyDown} // Handle Enter key press
              className={styles.itemsPerPageInput}
            />
          </div>
        </div>
      )}


      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <Modal
          title="确认删除"
          message="您确定要删除这条记录吗？此操作不可撤销。"
          actions={[
            { label: '确认', onClick: confirmDelete, className: styles.confirmButton },
            { label: '取消', onClick: () => setShowDeleteModal(false), className: styles.cancelButton },
          ]}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
}

export default ClassRecordList;
